import { createComponentStore } from '../../utils/createComponentStore';
import { JsDemoCode, PythonDemoCode } from './CodeComponent/AceEditorPlaceholder';

const NODE_JS_LANG_NAME = 'NodeJS';
const PYTHON_LANG_NAME = 'Python';
const CODE_PLACEHOLDER = 'CodePlaceholder';

const defaultPlaceholder = () => ({
  key: 'output1',
  label: 'output1',
  text: '',
  length: 1,
  style: {},
  type: 'text',
});

const initData = () => ({
  code: PythonDemoCode,
  language: 'Python_3_9',
  parameters: [],
  placeholders: [defaultPlaceholder()],
  version: 0,
  internetEnabled: false,
});

/**
 * Normalise raw data coming from the backend.
 * Handles demo-code placeholders, missing fields, and version coercion.
 */
const onLoadData = (rawData, component) => {
  const defaults = initData();
  const data = { ...defaults, ...rawData };

  // v1 backward compat: map codeLang → language
  if (data.codeLang && !rawData.language) {
    data.language = data.codeLang;
  }
  delete data.codeLang;

  // Replace code placeholder sentinel with actual demo code
  if (data.code === CODE_PLACEHOLDER) {
    if (data.language && data.language.includes(NODE_JS_LANG_NAME)) {
      data.code = JsDemoCode;
    } else if (data.language && data.language.includes(PYTHON_LANG_NAME)) {
      data.code = PythonDemoCode;
    }
  }

  if (!data.parameters) data.parameters = [];
  if (data.internetEnabled === undefined) data.internetEnabled = false;

  // Ensure at least one placeholder exists
  if (!data.placeholders || data.placeholders.length === 0) {
    data.placeholders = [defaultPlaceholder()];
  }

  if (data.code === undefined) data.code = PythonDemoCode;
  if (data.language === undefined) data.language = 'Python_3_9';

  return data;
};

const { getStore: getCodeStore, defaultStore } = createComponentStore({
  type: 'code',
  endpoint: 'code',
  registryKey: 'code',
  initData,
  onLoadData,

  extraState: (set, get) => ({
    position: null,
    placeholders: [],
    error: false,
  }),

  extraActions: (set, get) => ({
    // --- Override factory getTemplate to include position & placeholders ---
    getTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        position: s.position,
        type: s.type,
        lastModified: s.lastModified,
        data: JSON.parse(JSON.stringify(s.data)),
        placeholders: s.placeholders,
      };
    },

    // --- Override factory setTemplateData to reset extra state ---
    setTemplateData: (data) => {
      set({
        componentId: '',
        name: 'Run code',
        position: null,
        lastModified: '',
        placeholders: [],
        error: false,
        data,
      });
    },

    // --- Domain-specific actions ---

    deleteChip: (index) => {
      const newParams = [...get().data.parameters];
      newParams.splice(index, 1);
      set({ data: { ...get().data, parameters: newParams } });
    },

    addPlaceholders: (value) => {
      const current = get().placeholders;
      for (let i = 0; i < current.length; i++) {
        if (value.key && value.key === current[i].key) return;
      }
      set({ placeholders: [...current, value] });
    },

    deletePlaceholders: (index) => {
      const newPlaceholders = [...get().placeholders];
      newPlaceholders.splice(index, 1);
      set({ placeholders: newPlaceholders });
    },

    deleteCodePlaceholder: (index) => {
      const current = get().data.placeholders;
      if (current.length > index && index >= 0) {
        const newPlaceholders = [...current];
        newPlaceholders.splice(index, 1);
        set({ data: { ...get().data, placeholders: newPlaceholders } });
      }
    },

    getType: () => get().data.type,

    isInternetEnabledInStore: () => {
      const { data } = get();
      return data && data.internetEnabled ? data.internetEnabled : false;
    },

    getNewPlaceholderFromValue: (payload) => {
      const index = payload.index;
      const attribute = payload.attribute;
      let attributeValue = payload.value;

      const data = { ...get().data };
      let placeholders = [...(data.placeholders || [])];

      if (placeholders.length <= index) {
        placeholders.push({
          key: '', label: '', text: '', length: 1, style: {}, type: 'text',
        });
      }

      placeholders = placeholders.map((p, i) => {
        if (i !== index) return p;
        const updated = { ...p };
        switch (attribute) {
          case 'key': updated.key = attributeValue; break;
          case 'label': updated.label = attributeValue; break;
          case 'type': updated.type = attributeValue; break;
          case 'text': updated.text = attributeValue; break;
          case 'length':
            if (typeof attributeValue !== 'number') attributeValue = Number(attributeValue);
            updated.length = attributeValue;
            break;
          case 'style': updated.style = attributeValue; break;
        }
        return updated;
      });

      set({ data: { ...data, placeholders } });
      return placeholders;
    },

    setCode: (value, type) => {
      if (value === undefined) return;
      const data = { ...get().data };

      switch (type) {
        case 'code':
          data.code = value;
          break;
        case 'codeLang':
          data.language = value;
          break;
        case 'parameters': {
          if (data.parameters) {
            for (let i = 0; i < data.parameters.length; i++) {
              if (data.parameters[i] && data.parameters[i] === value) {
                return;
              }
            }
            data.parameters = [...data.parameters, value];
          }
          break;
        }
        case 'placeholders': {
          data.placeholders = get().getNewPlaceholderFromValue(value);
          return;
        }
        case 'version': {
          let v = value;
          if (typeof v !== 'number') v = Number(v);
          data.version = v;
          break;
        }
        case 'internetEnabled':
          data.internetEnabled = !!value;
          break;
        default:
          return;
      }
      set({ data });
    },

    resetStore: () => get().initialiseEmpty(),

    // --- Backward-compat aliases ---

    /**
     * setCodeData(component, currentVersion)
     * Delegates to the factory setComponentData but handles the version arg
     * and the empty-data path that needs a version-stamped default.
     */
    setCodeData: (component, currentVersion) => {
      const cd = component.componentData;
      const rawData = cd && cd.data;

      // If there is no real data, stamp the default with the provided version
      if (!rawData || typeof rawData !== 'object') {
        if (typeof currentVersion !== 'number') {
          currentVersion = Number(currentVersion);
        }
        if (Number.isNaN(currentVersion)) currentVersion = 0;

        const defaults = initData();
        defaults.version = currentVersion;

        const name = (cd && cd.name) || 'Run Code';
        const lastModified = (cd && cd.lastModified) || '';

        set({
          componentId: component.componentId,
          type: component.type || 'code',
          name,
          lastModified,
          data: defaults,
          loaded: true,
        });
        return;
      }

      // Has real data — delegate to factory setter, then mark loaded
      get().setComponentData(component);
      set({ loaded: true });
    },

    setCodeName: (name) => set({ name }),
  }),
});

// Attach constants for external consumers
defaultStore.NODE_JS_LANG_NAME = NODE_JS_LANG_NAME;
defaultStore.PYTHON_LANG_NAME = PYTHON_LANG_NAME;
defaultStore.CODE_PLACEHOLDER = CODE_PLACEHOLDER;

export { getCodeStore };
export default defaultStore;
